import './common-fields.scss';

import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FieldLabel from './FieldLabel';

export interface RadioButtonFieldProps<T extends string = string> {
  error?: string;
  id: string;
  label: string;
  labelTooltip?: string;
  options: FormSelectOptions<T>;
  required?: boolean;
  showError?: boolean;
  value: FormControlValue<T>;
  onChange: (val: T) => void;
}

export const RadioButtonField = <T extends string = string>(props: RadioButtonFieldProps<T>) => {
  const { error, id, label, labelTooltip, options, required, showError, value, onChange } = props;

  const labelId = `${id}-label`;
  const groupId = `${id}-group`;
  const errorId = `${id}-error`;
  const tooltipId = `${id}-label-tooltip`;

  return (
    <div className='po-field po-radio-field'>
      <div className='po-field-label-row'>
        <FieldLabel id={labelId} required={required}>
          {label}
        </FieldLabel>
        {labelTooltip ? (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id={tooltipId} className='po-field-info-tooltip'>
                {labelTooltip}
              </Tooltip>
            }
          >
            <button type='button' className='po-field-info' aria-label='More information'>
              <FontAwesomeIcon icon={faCircleInfo} aria-hidden='true' />
            </button>
          </OverlayTrigger>
        ) : null}
      </div>
      <div
        id={groupId}
        className='po-radio-group'
        role='radiogroup'
        aria-labelledby={labelId}
        aria-describedby={showError && error ? errorId : undefined}
        aria-invalid={showError ? true : undefined}
        aria-required={required ? true : undefined}
      >
        {options.map((option, index) => {
          const optionId = `${id}-${option.value}`;
          const isChecked = value === option.value;
          return (
            <label key={option.value} className='po-radio-option' htmlFor={optionId}>
              <input
                id={optionId}
                name={id}
                type='radio'
                value={option.value}
                checked={isChecked}
                required={!!required && index === 0}
                onChange={() => onChange(option.value)}
              />
              <span className='po-radio-label'>{option.label}</span>
            </label>
          );
        })}
      </div>
      {showError && error ? (
        <div id={errorId} className='po-error inline' role='status'>
          {error}
        </div>
      ) : null}
    </div>
  );
};
